import {
	eventHubService,
	Logger,
	trackingService,
} from '@monthio/react-components'
import { BehaviorSubject, combineLatest, skipWhile } from 'rxjs'

import type { HistoryWithEntries } from '../../routeOrchestrator'
import type { RootState } from '../../store/reducers'
import type { FlowModule } from './flowModules'

import { budgetApiClient } from '../../api/budgetApiClient'
import { ModuleDefinition } from '../../types/authenticateSmartcheckSessionDto'
import { loadingContainerService } from '../loadingContainerService'
import { tabManagerService } from '../tabManagerService'
import {
	deletedModule,
	mixpanelEventNameToModuleTypeMap,
	modulesInConfiguration,
	modulesWithoutFlowEvents,
} from './flowModules'
import { StorageService } from './StorageService'
import { userLeftService } from './userLeftService'

// Maybe call applicantCompleted action on finished module

// Handle async modules waiting for coapplicant to complete before starting non async modules

export const logger = new Logger('FlowOrchestrationService', {
	enabled: false,
	logLevel: 'debug',
})

class FlowOrchestrationService {
	readonly #currentModuleSubject = new BehaviorSubject<FlowModule | null>(null)
	readonly #availableModulesSubject = new BehaviorSubject<FlowModule[]>([])
	readonly #historyAvailableSubject = new BehaviorSubject<boolean | null>(null)

	#flowState: Record<string, { started: number; completed?: number }> = {}
	#applicantId: string | null = null
	#useApplicantFlow: boolean | null = null
	#applicantSessionId: string | null = null
	#storage: StorageService | null = null
	#history: HistoryWithEntries | null = null

	public setHistory = (history: HistoryWithEntries) => {
		this.#history = history
		const historyAvailable = history != null
		logger.debug('historyAvailable', historyAvailable)
		if (this.#historyAvailableSubject.value != historyAvailable) {
			this.#historyAvailableSubject.next(historyAvailable)
		}
	}

	public initialize = async () => {
		logger.debug('Initializing')
		tabManagerService.initialize()
		userLeftService.initialize()

		this.#storage = new StorageService()

		eventHubService.all.subscribe(async (event) => {
			logger.debug('event', event)
			switch (event.topic) {
				case 'moduleCompleted': {
					const { payload, source } = event
					const { module, skipped } = payload
					const isModuleCompleted = await this.#hasModuleBeenCompleted(module)
					console.log('moduleCompleted event received', { module, skipped })
					if (isModuleCompleted) {
						logger.debug('Module already completed, ignoring event', module)
						return
					}
					trackingService.trackTechnicalEvent(
						`moduleCompleted by ${source}`,
						payload,
					)
					loadingContainerService.show()
					console.log('loadingContainerService.show(): moduleCompleted', {
						module,
						skipped,
					})

					// Wait up to 10 seconds for finish module call to complete
					await Promise.race([
						this.FinishApplicantFlowModule(module, null),
						new Promise((resolve) => setTimeout(resolve, 10_000)),
					])

					await this.#completeModule(module, skipped)
					this.#startNextModule()
					break
				}
				case 'reopenModule': {
					const { payload } = event
					const { module: moduleName } = payload
					trackingService.trackTechnicalEvent('reopenModule', payload)

					const module = this.#availableModulesSubject.value.find(
						(m) =>
							m.type === moduleName && typeof m.reopenAction === 'function',
					)
					// TODO: Might need to route to a temp page while reopening is in progress
					if (typeof module?.reopenAction === 'function') {
						this.#history!.entries = []
						delete this.#flowState[module.type]
						for (const m of this.#availableModulesSubject.value) {
							if (m.moduleOrder > module.moduleOrder) {
								trackingService.trackTechnicalEvent(
									'Clearing state for module',
									m.type,
								)
								logger.debug('Clearing state for module', m.type)
								delete this.#flowState[m.type]
							}
						}
						logger.debug('Reopening module', module)
						await this.#startModule(module.type)
						const reopenableModules = await this.#getReopenableModules()
						await module.reopenAction(
							this.#history!,
							{
								reopenableModules,
							},
							event.payload,
						)
						this.#currentModuleSubject.next(module)
					}

					break
				}
			}
		})

		this.#currentModuleSubject.subscribe(async (module) => {
			logger.debug('CurrentModule', module)
			console.log('this.#currentModuleSubject.subscribe', module)
			if (module == null) {
				return
			}
			this.#startModule(module.type)
			if (module.synthetic) {
				eventHubService.onEvent({
					source: 'flowOrchestrator',
					topic: 'moduleCompleted',
					payload: { module: module.type },
				})
				return
			}
			this.#history!.entries = []
			const reopenableModules = await this.#getReopenableModules()
			logger.debug('Pushing route', module.route, reopenableModules)
			this.#history!.push(module.route, {
				reopenableModules,
			})
			loadingContainerService.hide()
			console.log(
				'loadingContainerService.hide()',
				'this.#currentModuleSubject.subscribe',
				module,
			)

			if (module.type === 'finished') {
				tabManagerService.completeFlow()
			}
		})

		combineLatest([
			this.#availableModulesSubject,
			this.#historyAvailableSubject,
		])
			.pipe(skipWhile(([, historyAvailable]) => !historyAvailable))
			.subscribe(([modules]) => {
				logger.debug('AvailableModules', modules)
				logger.debug('Starting')
				this.#startNextModule()
			})
	}

	#startModule = async (module: string) => {
		const hasModuleStarted = await this.#hasModuleBeenStarted(module)
		logger.debug('hasModuleStarted', { module, hasModuleStarted })
		if (hasModuleStarted) {
			return
		}
		this.#saveFlowProgress({ module, type: 'started' })
		const oldMixpanelEventName = mixpanelEventNameToModuleTypeMap.get(module)
		if (oldMixpanelEventName !== null) {
			trackingService.trackTechnicalEvent(`${module}:started`)
			this.#sendMixpanelEvent(oldMixpanelEventName ?? module, 'started')
		}
		this.#sendProgressEvent(module, 'started')
		await this.StartApplicantFlowModule(module, null)
	}

	#completeModule = async (module: string, skipped = false) => {
		const hasModuleFinished = await this.#hasModuleBeenCompleted(module)
		logger.debug('hasModuleFinished', { module, hasModuleFinished })
		if (hasModuleFinished) {
			return
		}
		this.#saveFlowProgress({ module, type: 'completed' })
		if (skipped) {
			logger.debug('module reported skip', module)
			return
		}
		const oldMixpanelEventName = mixpanelEventNameToModuleTypeMap.get(module)
		if (oldMixpanelEventName !== null) {
			trackingService.trackTechnicalEvent(`${module}:completed`)
			this.#sendMixpanelEvent(oldMixpanelEventName ?? module, 'completed')
		}
		await this.#sendProgressEvent(module, 'completed')
	}

	#hasModuleBeenStarted = async (module: string) => {
		const moduleState = this.#flowState[module]
		if (moduleState?.started != null) {
			return true
		}
		return false
	}

	#hasModuleBeenCompleted = async (module: string) => {
		const moduleState = this.#flowState[module]
		if (moduleState?.completed != null) {
			return true
		}
		return false
	}

	#startNextModule = async () => {
		logger.debug('startNextModule')
		logger.debug('availableModules', this.#availableModulesSubject.value)
		logger.debug('flowState', this.#flowState)

		if (this.#currentModuleSubject.value?.type === deletedModule.type) {
			return
		}

		for (const moduleIndex in this.#availableModulesSubject.value) {
			if (
				Object.prototype.hasOwnProperty.call(
					this.#availableModulesSubject.value,
					moduleIndex,
				)
			) {
				const module = this.#availableModulesSubject.value[moduleIndex]!
				const finished = await this.#hasModuleBeenCompleted(module.type)
				logger.debug('moduleIndex', {
					moduleIndex,
					module,
					completed: finished,
				})
				if (!finished) {
					if (module.type !== this.#currentModuleSubject.value?.type) {
						this.#currentModuleSubject.next(module)
					} else {
						// If this was called from the moduleCompleted event handler, loadingContainerServive.show() has already been called,
						// but currentModuleSubject has already been updated, the loading screen will appear forever. So we hide it here.
						// loadingContainerService.hide()
					}
					return
				}
			}
		}
	}

	#getReopenableModules = async () => {
		const completedModules = await Promise.all(
			this.#availableModulesSubject.value.map(async (module) => {
				if (
					typeof module.reopenAction === 'function' &&
					(await this.#hasModuleBeenCompleted(module.type))
				) {
					return module.type
				}
				return null
			}),
		)
		return completedModules.filter(Boolean) as string[]
	}

	#sendMixpanelEvent = (
		eventOrModuleName: string,
		state: 'started' | 'completed',
	) => {
		const now = Date.now()
		const { timeZone } = Intl.DateTimeFormat().resolvedOptions()
		switch (state) {
			case 'completed':
				trackingService.trackEvent(`Finished ${eventOrModuleName}`, {
					time: now,
					timeZone,
				})
				return
			case 'started':
				trackingService.trackEvent(`Started ${eventOrModuleName}`, {
					time: now,
					timeZone,
				})
		}
	}

	#sendProgressEvent = async (
		module: string,
		state: 'started' | 'completed',
	) => {
		if (modulesWithoutFlowEvents.has(module)) {
			logger.info('Skipping progress event for this module', module)
			return
		}
		switch (state) {
			case 'completed':
				await budgetApiClient.trackFlowProgressEvent(module, 'completed')
				return
			case 'started':
				await budgetApiClient.trackFlowProgressEvent(module, 'started')
		}
	}

	public readonly updateStoreState = async (state: RootState) => {
		logger.debug('FlowState', this.#flowState)
		logger.debug('updateStoreState', state)
		let nextAvailableModules: FlowModule[] = []
		this.#applicantId = state.authentication.applicantId
		this.#useApplicantFlow =
			state.authentication.smartCheckAuthenticationDto
				?.usesApplicantDomainFlow ?? null
		this.#applicantSessionId =
			state.authentication.smartCheckAuthenticationDto?.applicantSessionId ??
			null

		tabManagerService.setTab(this.#applicantId, this.#applicantSessionId)
		userLeftService.setApplicantData(
			this.#applicantId,
			this.#applicantSessionId,
		)
		if (
			this.#applicantId != null &&
			this.#applicantSessionId != null &&
			state.authentication.smartCheckAuthenticationDto != null
		) {
			this.#flowState = this.#storage!.get(this.#getStorageKey) ?? {}
			trackingService.trackTechnicalEvent(
				`flowState for ${this.#getStorageKey}`,
				JSON.stringify(this.#flowState),
			)
			logger.debug(
				'modules',
				state.authentication.smartCheckAuthenticationDto
					?.smartCheckConfiguration.moduleConfiguration.moduleIds,
			)
			const configurationDto = state.authentication.smartCheckAuthenticationDto
			logger.info(configurationDto)

			nextAvailableModules =
				configurationDto !== undefined
					? await modulesInConfiguration(configurationDto)
					: []
		}
		logger.debug('nextAvailableModules', nextAvailableModules)
		trackingService.trackTechnicalEvent(
			'availableModules',
			nextAvailableModules,
		)
		this.#availableModulesSubject.next(nextAvailableModules)
	}

	public readonly deleteSession = () => {
		logger.debug('deleteSession')
		this.#currentModuleSubject.next(deletedModule)
		this.#flowState = {}
		this.#storage!.set(this.#getStorageKey, this.#flowState)
	}

	#saveFlowProgress = ({
		module,
		type,
	}: {
		module: string
		type: 'started' | 'completed'
	}) => {
		if (this.#applicantId == null || this.#applicantSessionId == null) {
			console.warn('No applicantId or applicantSessionId, not saving')
			return
		}
		logger.info(module, type)
		this.#flowState[module] = {
			...this.#flowState[module]!,
			[type]: Date.now(),
		}
		logger.debug('Saving flowState', this.#flowState)
		this.#storage!.set(this.#getStorageKey, { ...this.#flowState })
	}

	private async FinishApplicantFlowModule(
		module: string,
		data: Record<string, unknown> | null,
	) {
		if (
			module == ModuleDefinition.IncomeVerification ||
			module == ModuleDefinition.Questionnaire ||
			module == ModuleDefinition.DebtOverview ||
			module == ModuleDefinition.Budget ||
			module == ModuleDefinition.SavingsCapacity ||
			module == ModuleDefinition.AssetExpenses ||
			module == ModuleDefinition.AssetOverview ||
			module == ModuleDefinition.CustomQuestionnaire
		) {
			// already done in api
			return
		}

		try {
			await budgetApiClient.finishFlowModuleWithApplicantDataApi(module, data)
		} catch (error) {
			trackingService.trackTechnicalEvent(
				`finishing module failed for ${module}`,
				{ err: error },
			)
		}
	}

	private async StartApplicantFlowModule(
		module: string,
		data: Record<string, unknown> | null,
	) {
		if (
			module == ModuleDefinition.Budget ||
			module == ModuleDefinition.DebtOverview ||
			module == ModuleDefinition.CustomQuestionnaire ||
			module == ModuleDefinition.IncomeVerification ||
			module == ModuleDefinition.AssetOverview ||
			module == ModuleDefinition.AssetExpenses
		) {
			// already done in api with real data
			return
		}
		await budgetApiClient.callApplicantDataAppStartModule(module, data)
	}

	get #getStorageKey() {
		if (this.#applicantId == null || this.#applicantSessionId == null) {
			return `flow-state::no-applicant-id:no-applicant-session-id`
		}
		return `flow-state::${this.#applicantId}:${this.#applicantSessionId}`
	}
}

export const flowOrchestrationService = new FlowOrchestrationService()
